// ===========================================
// Revenue Report Filters Component
// ===========================================
// Filter form for revenue reports with term, date range, and invoice status filters

import { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppDayjsLocalizationProvider } from '../common/AppDayjsLocalizationProvider';
import { FilterList as FilterIcon, Close as ClearIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

import { RevenueReportFilters } from '../../api/reports.api';
import { useTerms } from '../../hooks/useAdmin';

// ===========================================
// TYPES
// ===========================================

interface RevenueFiltersProps {
  filters: RevenueReportFilters;
  onFiltersChange: (filters: RevenueReportFilters) => void;
  onApply: () => void;
  onReset: () => void;
}

// Invoice status options
const INVOICE_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PAID', label: 'Paid' },
  { value: 'SENT', label: 'Unpaid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'OVERDUE', label: 'Overdue' },
];

// ===========================================
// COMPONENT
// ===========================================

export default function RevenueFilters({
  filters,
  onFiltersChange,
  onApply,
  onReset,
}: RevenueFiltersProps) {
  // Local state for date pickers (using dayjs)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    filters.endDate ? dayjs(filters.endDate) : null
  );

  // Fetch filter options
  const { data: terms = [] } = useTerms();

  // Filter to active terms only
  const activeTerms = terms.filter((term) => term.isActive);

  const handleFieldChange = (field: keyof RevenueReportFilters, value: string | undefined) => {
    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    onFiltersChange({
      ...filters,
      startDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    onFiltersChange({
      ...filters,
      endDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    onReset();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <AppDayjsLocalizationProvider>
          <Grid container spacing={2}>
            {/* Term Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Term"
                value={filters.termId || ''}
                onChange={(e) => handleFieldChange('termId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Terms</MenuItem>
                {activeTerms.map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* Invoice Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Invoice Status"
                value={filters.status || 'ALL'}
                onChange={(e) => handleFieldChange('status', e.target.value as any)}
                size="small"
              >
                {INVOICE_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleReset}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={onApply}
                >
                  Apply Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AppDayjsLocalizationProvider>
      </CardContent>
    </Card>
  );
}
